import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

let sessionIdCached: string | null = null;

function getSessionId(): string {
  if (sessionIdCached) return sessionIdCached;
  try {
    let sid = sessionStorage.getItem("nm_session_id");
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem("nm_session_id", sid);
    }
    sessionIdCached = sid;
    return sid;
  } catch {
    // sessionStorage may be unavailable in some contexts
    sessionIdCached = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return sessionIdCached;
  }
}

export interface CrossSellEventPayload {
  event_type: "impression" | "click";
  source: "pdp_fbt" | "cart" | "checkout";
  product_id?: string | number | null;
  recommended_product_id: string | number;
  position?: number;
  metadata?: Record<string, any>;
}

/**
 * Fire-and-forget cross-sell event logger. Best-effort: failures are silent
 * so analytics never block the checkout flow.
 */
export async function trackCrossSellEvent(payload: CrossSellEventPayload) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const row = {
      event_type: payload.event_type,
      source: payload.source,
      product_id: payload.product_id != null ? String(payload.product_id) : null,
      recommended_product_id: String(payload.recommended_product_id),
      position: payload.position ?? null,
      user_id: user?.id ?? null,
      session_id: getSessionId(),
      metadata: payload.metadata ?? {},
    };
    await supabase.from("cross_sell_events").insert(row as any);
  } catch {
    // Silent fail — analytics should never break UX.
  }
}

const IMPRESSION_SENT = new Set<string>();

/**
 * Hook that fires a cross-sell impression once per session+key when the
 * referenced element scrolls into view.
 */
export function useCrossSellImpression(
  ref: React.RefObject<HTMLElement>,
  payload: Omit<CrossSellEventPayload, "event_type"> & { key: string }
) {
  const firedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || firedRef.current) return;

    const dedupeKey = `${getSessionId()}::${payload.key}`;
    if (IMPRESSION_SENT.has(dedupeKey)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            IMPRESSION_SENT.add(dedupeKey);
            trackCrossSellEvent({
              event_type: "impression",
              source: payload.source,
              product_id: payload.product_id,
              recommended_product_id: payload.recommended_product_id,
              position: payload.position,
              metadata: payload.metadata,
            });
            observer.disconnect();
          }
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current, payload.key]);
}
